// src/services/syncEngine.ts
// Background sync engine — processes the offline mutation queue when connectivity returns.
// Handles file uploads, conflict resolution, retry with exponential backoff, and temp ID resolution.

import { db, SyncOperation, StoreName } from './offlineDb';
import { TOKEN_KEY } from '../constants';

// ── Event System ──

export type SyncEventType =
  | 'sync:started'
  | 'sync:completed'
  | 'sync:failed'
  | 'sync:conflict'
  | 'sync:allCompleted'
  | 'sync:progress'
  | 'sync:queueChanged';

export interface SyncEvent {
  type: SyncEventType;
  operation?: SyncOperation;
  result?: any;
  error?: string;
  summary?: { total: number; succeeded: number; failed: number };
}

type SyncEventHandler = (event: SyncEvent) => void;

const listeners = new Map<SyncEventType, Set<SyncEventHandler>>();

function emit(event: SyncEvent): void {
  const handlers = listeners.get(event.type);
  if (handlers) {
    handlers.forEach((h) => {
      try {
        h(event);
      } catch (e) {
        console.error('[SyncEngine] Listener error:', e);
      }
    });
  }
}

export function onSyncEvent(type: SyncEventType, handler: SyncEventHandler): () => void {
  if (!listeners.has(type)) {
    listeners.set(type, new Set());
  }
  listeners.get(type)!.add(handler);
  return () => {
    listeners.get(type)?.delete(handler);
  };
}

// ── Temp ID Resolution Map ──
// Maps tempId → real server ID after sync
const tempIdMap = new Map<string, string>();

export function getTempIdMapping(tempId: string): string | undefined {
  return tempIdMap.get(tempId);
}

export function getAllTempIdMappings(): Map<string, string> {
  return new Map(tempIdMap);
}

// ── Sync Engine State ──

let isSyncing = false;
let syncAborted = false;

export function isSyncActive(): boolean {
  return isSyncing;
}

// ── Core Sync Logic ──

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1000;

function getBackoffDelay(retryCount: number): number {
  return BASE_DELAY_MS * Math.pow(2, retryCount) + Math.random() * 500;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Build the full API URL from the operation's relative url */
function buildFullUrl(relativeUrl: string): string {
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';
  // If the URL already starts with http, use as-is (should not happen but guard)
  if (relativeUrl.startsWith('http')) return relativeUrl;
  // Remove leading slash duplication
  const cleanUrl = relativeUrl.startsWith('/') ? relativeUrl : `/${relativeUrl}`;
  return `${apiBase}${cleanUrl}`;
}

/** Get current auth headers */
function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Accept-Language': localStorage.getItem('appLanguage') || 'ar',
  };
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

/** Execute a single sync operation via fetch (bypass axios to avoid interceptor loops) */
async function executeSyncOperation(op: SyncOperation): Promise<Response> {
  const url = buildFullUrl(op.url);
  const headers: Record<string, string> = {
    ...getAuthHeaders(),
    ...(op.headers || {}),
  };

  let body: any;

  if (op.hasFiles && op.fileRefs?.length) {
    // Upload files as FormData
    const formData = new FormData();

    // Append regular data fields
    if (op.data) {
      for (const [key, value] of Object.entries(op.data)) {
        if (value !== undefined && value !== null) {
          if (typeof value === 'object' && !(value instanceof Blob)) {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, value as any);
          }
        }
      }
    }

    // Append file blobs
    for (const ref of op.fileRefs) {
      const pendingFile = await db.getFileByRef(ref);
      if (pendingFile) {
        formData.append(
          pendingFile.fieldName,
          pendingFile.blob,
          pendingFile.filename
        );
      }
    }

    body = formData;
    // Don't set Content-Type for FormData — browser sets it with boundary
  } else {
    // JSON payload
    if (op.data) {
      headers['Content-Type'] = 'application/json';
      body = typeof op.data === 'string' ? op.data : JSON.stringify(op.data);
    }
  }

  return fetch(url, {
    method: op.method,
    headers,
    body,
    credentials: 'include',
  });
}

/** Resolve client-side temporary IDs in pending sync queue operations */
async function resolveTempIdInQueue(tempId: string, realId: string): Promise<void> {
  const ops = await db.getAll<SyncOperation>('syncQueue');
  for (const op of ops) {
    if (op.status === 'pending' && op.id != null) {
      let modified = false;

      if (op.entityId === tempId) {
        op.entityId = realId;
        modified = true;
      }

      if (op.tempId === tempId) {
        op.tempId = realId;
        modified = true;
      }

      if (op.url.includes(tempId)) {
        op.url = op.url.replace(new RegExp(tempId, 'g'), realId);
        modified = true;
      }

      if (op.data) {
        const strData = JSON.stringify(op.data);
        if (strData.includes(tempId)) {
          try {
            const replacedStr = strData.replace(new RegExp(tempId, 'g'), realId);
            op.data = JSON.parse(replacedStr);
            modified = true;
          } catch (e) {
            console.error('[SyncEngine] Failed to parse replaced JSON data:', e);
          }
        }
      }

      if (modified) {
        await db.put('syncQueue', op);
      }
    }
  }
}

/** Process a single operation with retry logic */
async function processOperation(op: SyncOperation): Promise<boolean> {
  if (!op.id) return false;

  emit({ type: 'sync:started', operation: op });

  try {
    const response = await executeSyncOperation(op);

    if (response.ok) {
      // ✅ Success
      const result = await response.json().catch(() => null);

      // Resolve temp ID if the server returned a real ID
      if (op.tempId && result?.data?.id) {
        tempIdMap.set(op.tempId, result.data.id);
        await resolveTempIdInQueue(op.tempId, result.data.id);

        // Update local cache — replace temp entity with real data
        const storeName = getStoreNameForEntityType(op.entityType) as StoreName | null;
        if (storeName) {
          try {
            // Remove temp entry
            await db.delete(storeName, op.tempId);
            // Put real entry
            if (result.data) {
              await db.put(storeName, result.data);
            }
          } catch (e) {
            console.warn('[SyncEngine] Cache update after temp ID resolution failed:', e);
          }
        }
      } else if (op.entityId) {
        // Update cache with fresh server data
        const storeName = getStoreNameForEntityType(op.entityType) as StoreName | null;
        if (storeName && result?.data) {
          try {
            await db.put(storeName, result.data);
          } catch (e) {
            console.warn('[SyncEngine] Cache update failed:', e);
          }
        }
      }

      // Clean up pending files
      if (op.hasFiles && op.fileRefs) {
        for (const ref of op.fileRefs) {
          await db.removeFile(ref);
        }
      }

      await db.markCompleted(op.id);
      emit({ type: 'sync:completed', operation: op, result });
      return true;
    }

    if (response.status === 409) {
      // 🔀 Conflict — entity was already modified
      await db.markCompleted(op.id);
      emit({ type: 'sync:conflict', operation: op });
      return true; // Don't retry — conflict is handled
    }

    if (response.status === 401) {
      // 🔐 Unauthorized — pause sync, don't retry
      const errorText = 'Authentication expired — please log in again';
      await db.markFailed(op.id, errorText);
      emit({ type: 'sync:failed', operation: op, error: errorText });
      // Stop the whole sync to avoid spamming 401s
      syncAborted = true;
      return false;
    }

    // ❌ Other server error — retry with backoff
    const errorText = await response.text().catch(() => `HTTP ${response.status}`);
    if (op.retryCount < MAX_RETRIES) {
      op.status = 'pending';
      op.retryCount += 1;
      op.lastError = errorText;
      await db.put('syncQueue', op);
      // Wait with backoff then retry
      await sleep(getBackoffDelay(op.retryCount));
      return processOperation(op);
    } else {
      await db.markFailed(op.id, errorText);
      emit({ type: 'sync:failed', operation: op, error: errorText });
      return false;
    }
  } catch (err: any) {
    // Network error during sync attempt — retry later
    const errorMsg = err.message || 'Network error during sync';
    if (op.retryCount < MAX_RETRIES) {
      op.status = 'pending';
      op.retryCount += 1;
      op.lastError = errorMsg;
      await db.put('syncQueue', op);
      await sleep(getBackoffDelay(op.retryCount));
      return processOperation(op);
    } else {
      await db.markFailed(op.id!, errorMsg);
      emit({ type: 'sync:failed', operation: op, error: errorMsg });
      return false;
    }
  }
}

/** Map entity type to its IndexedDB store name */
function getStoreNameForEntityType(entityType: string): string | null {
  const map: Record<string, string> = {
    category: 'categories',
    dish: 'dishes',
    order: 'orders',
    table: 'tables',
    staff: 'staff',
    material: 'materials',
    inventoryCategory: 'inventoryCategories',
    recipeTemplate: 'recipeTemplates',
    expense: 'expenses',
    cashRegister: 'cashRegisters',
    shiftReport: 'shiftReports',
    restaurant: 'restaurants',
    restaurantProfile: 'restaurantProfile',
    deliveryCompany: 'deliveryCompanies',
    deliveryCompanyOrder: 'deliveryCompanyOrders',
  };
  return map[entityType] || null;
}

// ── Public API ──

/** Start processing the sync queue (called when going online) */
export async function startSync(): Promise<void> {
  if (isSyncing) return;

  isSyncing = true;
  syncAborted = false;

  let succeeded = 0;
  let failed = 0;
  let total = 0;

  try {
    // Process in FIFO order
    while (!syncAborted) {
      const op = await db.dequeue();
      if (!op) break; // Queue is empty

      total++;
      emit({
        type: 'sync:progress',
        summary: { total, succeeded, failed },
      });

      const success = await processOperation(op);
      if (success) {
        succeeded++;
      } else {
        failed++;
      }
    }

    // Clean up completed entries
    await db.removeCompleted();

    // Emit final summary
    emit({
      type: 'sync:allCompleted',
      summary: { total, succeeded, failed },
    });

    // Notify queue changed so UI can update counts
    emit({ type: 'sync:queueChanged' });
  } catch (err) {
    console.error('[SyncEngine] Unexpected error during sync:', err);
  } finally {
    isSyncing = false;
  }
}

/** Retry all failed operations */
export async function retryFailed(): Promise<void> {
  await db.resetFailed();
  emit({ type: 'sync:queueChanged' });
  await startSync();
}

/** Clear all failed operations from the queue */
export async function clearFailed(): Promise<void> {
  const failed = await db.getFailed();
  for (const op of failed) {
    if (op.id != null) {
      await db.delete('syncQueue', op.id);
    }
  }
  emit({ type: 'sync:queueChanged' });
}

// ── Auto-sync on connectivity ──

let onlineListenerRegistered = false;
const PERIODIC_SYNC_INTERVAL_MS = 10_000; // 10 seconds

/** Check if the backend API is reachable */
async function isBackendReachable(): Promise<boolean> {
  try {
    const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';
    const healthUrl = apiBase.replace(/\/api\/v\d+$/, '') + '/api/v1/health';
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    const res = await fetch(healthUrl, {
      method: 'GET',
      cache: 'no-store',
      signal: controller.signal,
    });
    clearTimeout(timeout);
    return res.ok;
  } catch {
    return false;
  }
}

export function registerAutoSync(): void {
  if (onlineListenerRegistered) return;
  onlineListenerRegistered = true;

  // 1. Browser online event (handles WiFi/Ethernet reconnect)
  window.addEventListener('online', () => {
    console.log('[SyncEngine] Browser online event fired — will attempt sync');
    setTimeout(async () => {
      if (navigator.onLine && await isBackendReachable()) {
        console.log('[SyncEngine] Backend reachable — starting sync');
        startSync();
      }
    }, 1500);
  });

  // 2. Periodic sync check — handles backend restarts where browser stays "online"
  //    The browser 'online' event only fires for physical network changes,
  //    NOT when a backend server is stopped/restarted on the same network.
  setInterval(async () => {
    if (isSyncing) return;

    try {
      const pending = await db.pendingCount();
      if (pending === 0) return;

      console.log(`[SyncEngine] Periodic check: ${pending} pending ops — checking backend...`);
      const reachable = await isBackendReachable();
      if (reachable) {
        console.log('[SyncEngine] Backend reachable — starting sync');
        startSync();
      } else {
        console.log('[SyncEngine] Backend not reachable — will retry later');
      }
    } catch (err) {
      // Silently continue — will retry next interval
    }
  }, PERIODIC_SYNC_INTERVAL_MS);
}

export default {
  startSync,
  retryFailed,
  clearFailed,
  registerAutoSync,
  onSyncEvent,
  isSyncActive,
  getTempIdMapping,
  getAllTempIdMappings,
};
